import React from 'react';
import { useNavigationType } from 'react-router-dom';

/**
 * Returns a callback ref to attach to a scrollable element.
 * - On browser back (POP): restores the saved scroll position.
 * - While on the page: continuously saves the scroll position.
 * - On intentional navigation (PUSH/REPLACE): clears the saved position.
 */
export const useRestoreScrollPositionOnBack = (storageKey: string): React.RefCallback<HTMLElement> => {
  const navigationType = useNavigationType();
  const [element, setElement] = React.useState<HTMLElement | null>(null);

  const callbackRef = React.useCallback((el: HTMLElement | null) => {
    setElement(el);
  }, []);

  // On fresh forward navigation (PUSH), wipe the stored position so a
  // subsequent browser-back doesn't restore a stale offset.
  // REPLACE navigations are same-page URL updates (filters, load-more) and
  // must not clear the position — otherwise load-more wipes scroll state.
  React.useEffect(() => {
    if (navigationType === 'PUSH') {
      sessionStorage.removeItem(storageKey);
    }
  }, [navigationType, storageKey]);

  // Restore scroll position when the element mounts on a back navigation.
  // Uses a ResizeObserver to re-apply the target if the content is initially
  // too short (e.g. SWR revalidation hasn't finished loading subsequent pages yet).
  React.useEffect(() => {
    if (!element || navigationType !== 'POP') return;
    const saved = sessionStorage.getItem(storageKey);
    if (saved === null) return;
    const target = Number(saved);

    element.scrollTop = target;
    if (element.scrollTop >= target - 1) return;

    // Content not tall enough yet — re-try each time the inner content grows.
    const content = element.firstElementChild;
    if (!content) return;

    let lastScrollHeight = element.scrollHeight;

    const observer = new ResizeObserver(() => {
      const currentScrollHeight = element.scrollHeight;
      element.scrollTop = target;

      if (element.scrollTop >= target - 1) {
        observer.disconnect(); // target reached
        return;
      }

      if (currentScrollHeight <= lastScrollHeight) {
        observer.disconnect(); // content stabilised but target is unreachable — give up
        return;
      }

      lastScrollHeight = currentScrollHeight;
    });
    observer.observe(content);

    return () => observer.disconnect();
  }, [element, navigationType, storageKey]);

  // Save scroll position continuously while on the page.
  React.useEffect(() => {
    if (!element) return;
    const handler = () => { sessionStorage.setItem(storageKey, String(element.scrollTop)); };
    element.addEventListener('scroll', handler);
    return () => element.removeEventListener('scroll', handler);
  }, [element, storageKey]);

  return callbackRef;
};
